<script>
import TitleWithLinesBlock from '@/components/newDesign/title-with-lines-block.vue';

export default {
  name: 'EditTitleWithLinesModal',
  components: {
    TitleWithLinesBlock,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      default: () => [],
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['save', 'cancel', 'delete'],
  data() {
    return {
      localItems: [],
      currentItemIndex: 0,
      currentViewMode: 'desktop', // 'desktop' или 'mobile'
      previewKey: 0,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    currentItem() {
      return this.localItems[this.currentItemIndex];
    },
    modalWidth() {
      if (this.windowWidth > 1920) {
        return '1640px';
      }
      if (this.windowWidth > 1400) {
        return '1200px';
      }
      return '900px';
    },
    previewBackgroundColor() {
      if (!this.currentItem) return '#f5f5f5';
      const color = this.currentViewMode === 'mobile'
        ? this.currentItem.colorMob
        : this.currentItem.color;
      const isLightColor = this.isColorLight(color);
      return isLightColor ? '#293241' : '#ffffff';
    },
    desktopAlignment: {
      get() {
        if (this.currentItem.isLeft) return 'left';
        if (this.currentItem.isRight) return 'right';
        return 'center';
      },
      set(value) {
        this.currentItem.isLeft = value === 'left';
        this.currentItem.isRight = value === 'right';
        this.updatePreview();
      },
    },
    mobileAlignment: {
      get() {
        if (this.currentItem.isLeftMob) return 'left';
        if (this.currentItem.isRightMob) return 'right';
        return 'center';
      },
      set(value) {
        this.currentItem.isLeftMob = value === 'left';
        this.currentItem.isRightMob = value === 'right';
        this.updatePreview();
      },
    },
    previewItems() {
      const isMobile = this.currentViewMode === 'mobile';
      return this.localItems.map((currentItem) => {
        const item = JSON.parse(JSON.stringify(currentItem));
        // Hide first text line if no direction selected
        if (isMobile) {
          if (item.isTextWithLineMob && item.firstTextLines && item.firstTextLines[0]) {
            const line = item.firstTextLines[0];
            if (!line.isLeftLineMob && !line.isRightLineMob
              && !line.isTopLineMob && !line.isBottomLineMob) {
              item.isTextWithLineMob = false;
            }
          }
          if (item.isSecondTextWithLineMob && item.secondTextLines && item.secondTextLines[0]) {
            const line = item.secondTextLines[0];
            if (!line.isLeftLineMob && !line.isRightLineMob
              && !line.isTopLineMob && !line.isBottomLineMob) {
              item.isSecondTextWithLineMob = false;
            }
          }
        } else {
          if (item.isTextWithLine && item.firstTextLines && item.firstTextLines[0]) {
            const line = item.firstTextLines[0];
            if (!line.isLeftLine && !line.isRightLine && !line.isTopLine && !line.isBottomLine) {
              item.isTextWithLine = false;
            }
          }
          if (item.isSecondTextWithLine && item.secondTextLines && item.secondTextLines[0]) {
            const line = item.secondTextLines[0];
            if (!line.isLeftLine && !line.isRightLine && !line.isTopLine && !line.isBottomLine) {
              item.isSecondTextWithLine = false;
            }
          }
        }
        return item;
      });
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initializeData();
      }
    },
    currentViewMode() {
      this.updatePreview();
    },
    data: {
      handler() {
        if (this.visible) {
          this.initializeData();
        }
      },
      deep: true,
    },
  },
  methods: {
    // Convert shorthand hex (#fff) to full hex (#ffffff) for color picker
    normalizeHexColor(color) {
      if (!color || typeof color !== 'string') return null;
      const trimmed = color.trim();
      if (!trimmed.startsWith('#')) return null;
      // If shorthand like #fff, expand to #ffffff
      if (trimmed.length === 4) {
        const r = trimmed[1];
        const g = trimmed[2];
        const b = trimmed[3];
        return `#${r}${r}${g}${g}${b}${b}`;
      }
      // Already full format
      if (trimmed.length === 7) {
        return trimmed;
      }
      return null;
    },
    initializeData() {
      if (this.data && this.data.length > 0) {
        // Merge existing data with default values to ensure all fields exist
        this.localItems = this.data.map((item) => {
          const defaultItem = this.createEmptyItem();
          const defaultLine = this.createEmptyLine();

          // Merge first text lines
          const firstTextLines = (item.firstTextLines || []).map((line) => {
            const merged = {
              ...defaultLine,
              ...line,
              id: line.id || Date.now() + Math.random(),
            };
            // Normalize hex color for color picker (expand #fff to #ffffff)
            merged.color = this.normalizeHexColor(merged.color) || defaultLine.color;
            merged.colorMob = this.normalizeHexColor(merged.colorMob) || defaultLine.colorMob;
            return merged;
          });

          // Merge second text lines
          const secondTextLines = (item.secondTextLines || []).map((line) => {
            const merged = {
              ...defaultLine,
              ...line,
              id: line.id || Date.now() + Math.random(),
            };
            // Normalize hex color for color picker (expand #fff to #ffffff)
            merged.color = this.normalizeHexColor(merged.color) || defaultLine.color;
            merged.colorMob = this.normalizeHexColor(merged.colorMob) || defaultLine.colorMob;
            return merged;
          });

          const merged = {
            ...defaultItem,
            ...item,
            id: item.id || Date.now() + Math.random(),
            firstTextLines,
            secondTextLines,
          };

          // Normalize hex color for color picker (expand #fff to #ffffff)
          merged.color = this.normalizeHexColor(merged.color) || defaultItem.color;
          merged.colorMob = this.normalizeHexColor(merged.colorMob) || defaultItem.colorMob;

          return merged;
        });
      } else {
        this.localItems = [this.createEmptyItem()];
      }
      this.currentItemIndex = 0;
      this.currentViewMode = 'desktop';
    },
    createEmptyItem() {
      return {
        id: Date.now(),
        text: '',
        textMob: '',
        secondText: '',
        secondTextMob: '',
        isTextReverse: false,
        isTextReverseMob: false,
        isTextColumn: false,
        isTextColumnMob: false,
        isLeft: false,
        isLeftMob: false,
        isRight: false,
        isRightMob: false,
        color: '#000000',
        colorMob: '#000000',
        isTextWithLine: false,
        isTextWithLineMob: false,
        isSecondTextWithLine: false,
        isSecondTextWithLineMob: false,
        firstTextLines: [],
        secondTextLines: [],
      };
    },
    createEmptyLine() {
      return {
        id: Date.now(),
        isLeftLine: false,
        isRightLine: false,
        isTopLine: false,
        isBottomLine: false,
        color: '#000000',
        isLineWithSquare: false,
        isLeftLineMob: false,
        isRightLineMob: false,
        isTopLineMob: false,
        isBottomLineMob: false,
        colorMob: '#000000',
        isLineWithSquareMob: false,
      };
    },
    addItem() {
      if (this.localItems.length < 3) {
        this.localItems.push(this.createEmptyItem());
        this.currentItemIndex = this.localItems.length - 1;
      }
    },
    removeItem(index) {
      if (this.localItems.length > 1) {
        this.localItems.splice(index, 1);
        if (this.currentItemIndex >= this.localItems.length) {
          this.currentItemIndex = this.localItems.length - 1;
        }
      }
    },
    toggleFirstTextLine() {
      if (this.currentItem.isTextWithLine) {
        if (!this.currentItem.firstTextLines || this.currentItem.firstTextLines.length === 0) {
          this.currentItem.firstTextLines = [this.createEmptyLine()];
        }
      } else {
        this.currentItem.firstTextLines = [];
      }
      this.updatePreview();
    },
    toggleSecondTextLine() {
      if (this.currentItem.isSecondTextWithLine) {
        if (!this.currentItem.secondTextLines || this.currentItem.secondTextLines.length === 0) {
          this.currentItem.secondTextLines = [this.createEmptyLine()];
        }
      } else {
        this.currentItem.secondTextLines = [];
      }
      this.updatePreview();
    },
    toggleFirstTextLineMobile() {
      if (this.currentItem.isTextWithLineMob) {
        if (!this.currentItem.firstTextLines || this.currentItem.firstTextLines.length === 0) {
          this.currentItem.firstTextLines = [this.createEmptyLine()];
        }
      }
      this.updatePreview();
    },
    toggleSecondTextLineMobile() {
      if (this.currentItem.isSecondTextWithLineMob) {
        if (!this.currentItem.secondTextLines || this.currentItem.secondTextLines.length === 0) {
          this.currentItem.secondTextLines = [this.createEmptyLine()];
        }
      }
      this.updatePreview();
    },
    updatePreview() {
      this.previewKey += 1;
    },
    hasAnyContent() {
      return this.localItems.some((item) => item.text
        || item.textMob
        || item.secondText
        || item.secondTextMob);
    },
    validateLines() {
      const errors = [];
      this.localItems.forEach((item, index) => {
        const itemLabel = this.localItems.length > 1 ? ` (Item ${index + 1})` : '';

        if (item.isTextWithLine && item.firstTextLines && item.firstTextLines[0]) {
          const line = item.firstTextLines[0];
          if (!line.isLeftLine && !line.isRightLine && !line.isTopLine && !line.isBottomLine) {
            errors.push(`First text line is enabled but no direction selected${itemLabel} (Desktop)`);
          }
        }
        if (item.isSecondTextWithLine && item.secondTextLines && item.secondTextLines[0]) {
          const line = item.secondTextLines[0];
          if (!line.isLeftLine && !line.isRightLine && !line.isTopLine && !line.isBottomLine) {
            errors.push(`Second text line is enabled but no direction selected${itemLabel} (Desktop)`);
          }
        }

        if (item.isTextWithLineMob && item.firstTextLines && item.firstTextLines[0]) {
          const line = item.firstTextLines[0];
          if (!line.isLeftLineMob && !line.isRightLineMob
            && !line.isTopLineMob && !line.isBottomLineMob) {
            errors.push(`First text line is enabled but no direction selected${itemLabel} (Mobile)`);
          }
        }
        if (item.isSecondTextWithLineMob && item.secondTextLines && item.secondTextLines[0]) {
          const line = item.secondTextLines[0];
          if (!line.isLeftLineMob && !line.isRightLineMob
            && !line.isTopLineMob && !line.isBottomLineMob) {
            errors.push(`Second text line is enabled but no direction selected${itemLabel} (Mobile)`);
          }
        }
      });
      return errors;
    },
    handleSave() {
      if (!this.hasAnyContent()) {
        this.$emit('cancel');
        return;
      }
      const validationErrors = this.validateLines();
      if (validationErrors.length > 0) {
        this.$message.warning(validationErrors[0]);
        return;
      }
      this.$emit('save', this.localItems);
    },
    handleCancel() {
      this.$emit('cancel');
    },
    handleDelete() {
      this.$emit('delete');
    },
    isColorLight(hexColor) {
      if (!hexColor) return false;
      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5;
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    addFirstTextLine() {
      if (!this.currentItem.firstTextLines) {
        this.currentItem.firstTextLines = [];
      }
      this.currentItem.firstTextLines.push(this.createEmptyLine());
      this.updatePreview();
    },
    removeFirstTextLine(index) {
      this.currentItem.firstTextLines.splice(index, 1);
      this.updatePreview();
    },
    addSecondTextLine() {
      if (!this.currentItem.secondTextLines) {
        this.currentItem.secondTextLines = [];
      }
      this.currentItem.secondTextLines.push(this.createEmptyLine());
      this.updatePreview();
    },
    removeSecondTextLine(index) {
      this.currentItem.secondTextLines.splice(index, 1);
      this.updatePreview();
    },
    getLineHorizontalPosition(line) {
      if (line.isLeftLine) return 'left';
      if (line.isRightLine) return 'right';
      return 'none';
    },
    setLineHorizontalPosition(lineArray, lineIndex, value) {
      const updated = { ...lineArray[lineIndex], isLeftLine: value === 'left', isRightLine: value === 'right' };
      lineArray.splice(lineIndex, 1, updated);
      this.updatePreview();
    },
    getLineHorizontalPositionMob(line) {
      if (line.isLeftLineMob) return 'left';
      if (line.isRightLineMob) return 'right';
      return 'none';
    },
    setLineHorizontalPositionMob(lineArray, lineIndex, value) {
      const updated = { ...lineArray[lineIndex], isLeftLineMob: value === 'left', isRightLineMob: value === 'right' };
      lineArray.splice(lineIndex, 1, updated);
      this.updatePreview();
    },
    getLineVerticalPositionMob(line) {
      if (line.isTopLineMob) return 'top';
      if (line.isBottomLineMob) return 'bottom';
      return 'none';
    },
    setLineVerticalPositionMob(lineArray, lineIndex, value) {
      const updated = { ...lineArray[lineIndex], isTopLineMob: value === 'top', isBottomLineMob: value === 'bottom' };
      lineArray.splice(lineIndex, 1, updated);
      this.updatePreview();
    },
  },
};
</script>

<template>
  <a-modal
    :open="visible"
    title="Edit Title With Lines"
    :width="modalWidth"
    :z-index="2000"
    @cancel="handleCancel"
    :footer="null"
    class="title-lines-modal"
  >
    <div class="title-lines-editor">
      <div class="items-list">
        <button
          v-for="(item, index) in localItems"
          :key="index"
          type="button"
          :class="['item-tab', { active: currentItemIndex === index }]"
          @click="currentItemIndex = index"
        >
          Item {{ index + 1 }}
        </button>
        <a-button
          v-if="localItems.length < 3"
          type="dashed"
          size="small"
          @click="addItem"
        >
          + Add Item
        </a-button>
      </div>

      <div v-if="localItems[currentItemIndex]" class="item-editor">
        <a-tabs v-model:activeKey="currentViewMode" class="view-tabs">
          <a-tab-pane key="desktop" tab="Desktop" force-render>
            <div class="editor-content">
              <div class="text-fields">
                <div class="field-group">
                  <span class="field-label">First Text (Desktop)</span>
                  <a-textarea
                    v-model:value="currentItem.text"
                    placeholder="Enter first text"
                    :auto-size="{ minRows: 1, maxRows: 4 }"
                    @input="updatePreview"
                  />
                </div>
                <div class="field-group">
                  <span class="field-label">Second Text (Desktop)</span>
                  <a-textarea
                    v-model:value="currentItem.secondText"
                    placeholder="Enter second text"
                    :auto-size="{ minRows: 1, maxRows: 4 }"
                    @input="updatePreview"
                  />
                </div>
              </div>

              <div class="layout-settings">
                <h4>Layout Settings (Desktop)</h4>
                <div class="checkbox-row">
                  <a-checkbox v-model:checked="currentItem.isTextColumn" @change="updatePreview">
                    Column Layout
                  </a-checkbox>
                  <a-checkbox v-model:checked="currentItem.isTextReverse" @change="updatePreview">
                    Reverse Order
                  </a-checkbox>
                </div>
                <div class="alignment-row">
                  <span>Alignment:</span>
                  <a-radio-group v-model:value="desktopAlignment" @change="updatePreview">
                    <a-radio-button value="left">Left</a-radio-button>
                    <a-radio-button value="center">Center</a-radio-button>
                    <a-radio-button value="right">Right</a-radio-button>
                  </a-radio-group>
                </div>
              </div>

              <div class="color-picker-group">
                <span class="field-label">Text Color (Desktop)</span>
                <a-input
                  v-model:value="currentItem.color"
                  type="color"
                  class="color-input"
                  @input="updatePreview"
                />
                <span class="color-value">{{ currentItem.color }}</span>
              </div>

              <div class="lines-config">
                <div class="lines-header">
                  <h4>First Text Lines</h4>
                  <a-checkbox
                    v-model:checked="currentItem.isTextWithLine"
                    @change="toggleFirstTextLine"
                  >
                    Show Lines
                  </a-checkbox>
                  <a-button
                    v-if="currentItem.isTextWithLine"
                    type="dashed"
                    size="small"
                    @click="addFirstTextLine"
                  >
                    + Add Line
                  </a-button>
                </div>
                <div v-if="currentItem.isTextWithLine && currentItem.firstTextLines.length" >
                  <div
                    v-for="(line, lineIndex) in currentItem.firstTextLines"

                    :key="line.id"
                    class="line-settings"
                  >
                    <div class="line-header">
                      <span class="line-label">Line {{ lineIndex + 1 }}</span>
                      <a-button
                        v-if="currentItem.firstTextLines.length > 1"
                        type="text"
                        danger
                        size="small"
                        @click="removeFirstTextLine(lineIndex)"
                      >
                        Remove
                      </a-button>
                    </div>
                    <div class="line-position">
                      <span>Side:</span>
                      <a-radio-group
                        :value="getLineHorizontalPosition(line)"
                        @change="(e) => setLineHorizontalPosition(
                          currentItem.firstTextLines, lineIndex, e.target.value,
                        )"
                      >
                        <a-radio-button value="left">Left</a-radio-button>
                        <a-radio-button value="right">Right</a-radio-button>
                      </a-radio-group>
                    </div>
                    <div class="line-options">
                      <a-checkbox
                        v-model:checked="line.isLineWithSquare"
                        @change="updatePreview"
                      >
                        With Diamond
                      </a-checkbox>
                      <span class="field-label">Line Color:</span>
                      <a-input
                        v-model:value="line.color"
                        type="color"
                        class="color-input"
                        @input="updatePreview"
                      />
                    </div>
                  </div>
                </div>

                <div class="lines-header">
                  <h4>Second Text Lines</h4>
                  <a-checkbox
                    v-model:checked="currentItem.isSecondTextWithLine"
                    @change="toggleSecondTextLine"
                  >
                    Show Lines
                  </a-checkbox>
                  <a-button
                    v-if="currentItem.isSecondTextWithLine"
                    type="dashed"
                    size="small"
                    @click="addSecondTextLine"
                  >
                    + Add Line
                  </a-button>
                </div>
                <div v-if="currentItem.isSecondTextWithLine && currentItem.secondTextLines.length" >
                  <div
                    v-for="(line, lineIndex) in currentItem.secondTextLines"

                    :key="line.id"
                    class="line-settings"
                  >
                    <div class="line-header">
                      <span class="line-label">Line {{ lineIndex + 1 }}</span>
                      <a-button
                        v-if="currentItem.secondTextLines.length > 1"
                        type="text"
                        danger
                        size="small"
                        @click="removeSecondTextLine(lineIndex)"
                      >
                        Remove
                      </a-button>
                    </div>
                    <div class="line-position">
                      <span>Side:</span>
                      <a-radio-group
                        :value="getLineHorizontalPosition(line)"
                        @change="(e) => setLineHorizontalPosition(
                          currentItem.secondTextLines, lineIndex, e.target.value,
                        )"
                      >
                        <a-radio-button value="left">Left</a-radio-button>
                        <a-radio-button value="right">Right</a-radio-button>
                      </a-radio-group>
                    </div>
                    <div class="line-options">
                      <a-checkbox
                        v-model:checked="line.isLineWithSquare"
                        @change="updatePreview">
                        With Diamond
                      </a-checkbox>
                      <span class="field-label">Line Color:</span>
                      <a-input
                        v-model:value="line.color"
                        type="color"
                        class="color-input"
                        @input="updatePreview"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a-tab-pane>

          <a-tab-pane key="mobile" tab="Mobile" force-render>
            <div class="editor-content">
              <div class="text-fields">
                <div class="field-group">
                  <span class="field-label">First Text (Mobile)</span>
                  <a-textarea
                    v-model:value="currentItem.textMob"
                    placeholder="Enter first text for mobile"
                    :auto-size="{ minRows: 1, maxRows: 4 }"
                    @input="updatePreview"
                  />
                </div>
                <div class="field-group">
                  <span class="field-label">Second Text (Mobile)</span>
                  <a-textarea
                    v-model:value="currentItem.secondTextMob"
                    placeholder="Enter second text for mobile"
                    :auto-size="{ minRows: 1, maxRows: 4 }"
                    @input="updatePreview"
                  />
                </div>
              </div>

              <div class="layout-settings">
                <h4>Layout Settings (Mobile)</h4>
                <div class="checkbox-row">
                  <a-checkbox
                    v-model:checked="currentItem.isTextColumnMob"
                    @change="updatePreview"
                  >
                    Column Layout
                  </a-checkbox>
                  <a-checkbox
                    v-model:checked="currentItem.isTextReverseMob"
                    @change="updatePreview"
                  >
                    Reverse Order
                  </a-checkbox>
                </div>
                <div class="alignment-row">
                  <span>Alignment:</span>
                  <a-radio-group v-model:value="mobileAlignment" @change="updatePreview">
                    <a-radio-button value="left">Left</a-radio-button>
                    <a-radio-button value="center">Center</a-radio-button>
                    <a-radio-button value="right">Right</a-radio-button>
                  </a-radio-group>
                </div>
              </div>

              <div class="color-picker-group">
                <span class="field-label">Text Color (Mobile)</span>
                <a-input
                  v-model:value="currentItem.colorMob"
                  type="color"
                  class="color-input"
                  @input="updatePreview"
                />
                <span class="color-value">{{ currentItem.colorMob }}</span>
              </div>

              <div class="lines-config">
                <div class="lines-header">
                  <h4>First Text Lines (Mobile)</h4>
                  <a-checkbox
                    v-model:checked="currentItem.isTextWithLineMob"
                    @change="toggleFirstTextLineMobile"
                  >
                    Show Lines
                  </a-checkbox>
                  <a-button
                    v-if="currentItem.isTextWithLineMob"
                    type="dashed"
                    size="small"
                    @click="addFirstTextLine"
                  >
                    + Add Line
                  </a-button>
                </div>
                <div v-if="currentItem.isTextWithLineMob && currentItem.firstTextLines.length" >
                  <div
                    v-for="(line, lineIndex) in currentItem.firstTextLines"
                    :key="line.id"
                    class="line-settings"
                  >
                    <div class="line-header">
                      <span class="line-label">Line {{ lineIndex + 1 }}</span>
                      <a-button
                        v-if="currentItem.firstTextLines.length > 1"
                        type="text"
                        danger
                        size="small"
                        @click="removeFirstTextLine(lineIndex)"
                      >
                        Remove
                      </a-button>
                    </div>
                    <div class="line-position">
                      <span>Side:</span>
                      <a-radio-group
                        :value="getLineHorizontalPositionMob(line)"
                        @change="(e) => setLineHorizontalPositionMob(
                          currentItem.firstTextLines, lineIndex, e.target.value,
                        )"
                      >
                        <a-radio-button value="left">Left</a-radio-button>
                        <a-radio-button value="right">Right</a-radio-button>
                      </a-radio-group>
                      <span>Level:</span>
                      <a-radio-group
                        :value="getLineVerticalPositionMob(line)"
                        @change="(e) => setLineVerticalPositionMob(
                          currentItem.firstTextLines, lineIndex, e.target.value,
                        )"
                      >
                        <a-radio-button value="top">Top</a-radio-button>
                        <a-radio-button value="bottom">Bottom</a-radio-button>
                        <a-radio-button value="none">Center</a-radio-button>
                      </a-radio-group>
                    </div>
                    <div class="line-options">
                      <a-checkbox
                        v-model:checked="line.isLineWithSquareMob"
                        @change="updatePreview"
                      >
                        With Diamond
                      </a-checkbox>
                      <span class="field-label">Line Color:</span>
                      <a-input
                        v-model:value="line.colorMob"
                        type="color"
                        class="color-input"
                        @input="updatePreview"
                      />
                    </div>
                  </div>
                </div>

                <div class="lines-header">
                  <h4>Second Text Lines (Mobile)</h4>
                  <a-checkbox
                    v-model:checked="currentItem.isSecondTextWithLineMob"
                    @change="toggleSecondTextLineMobile"
                  >
                    Show Lines
                  </a-checkbox>
                  <a-button
                    v-if="currentItem.isSecondTextWithLineMob"
                    type="dashed"
                    size="small"
                    @click="addSecondTextLine"
                  >
                    + Add Line
                  </a-button>
                </div>
                <div
                  v-if="currentItem.isSecondTextWithLineMob && currentItem.secondTextLines.length"
                >
                  <div
                    v-for="(line, lineIndex) in currentItem.secondTextLines"
                    :key="line.id"
                    class="line-settings"
                  >
                    <div class="line-header">
                      <span class="line-label">Line {{ lineIndex + 1 }}</span>
                      <a-button
                        v-if="currentItem.secondTextLines.length > 1"
                        type="text"
                        danger
                        size="small"
                        @click="removeSecondTextLine(lineIndex)"
                      >
                        Remove
                      </a-button>
                    </div>
                    <div class="line-position">
                      <span>Side:</span>
                      <a-radio-group
                        :value="getLineHorizontalPositionMob(line)"
                        @change="(e) => setLineHorizontalPositionMob(
                          currentItem.secondTextLines, lineIndex, e.target.value,
                        )"
                      >
                        <a-radio-button value="left">Left</a-radio-button>
                        <a-radio-button value="right">Right</a-radio-button>
                      </a-radio-group>
                      <span>Level:</span>
                      <a-radio-group
                        :value="getLineVerticalPositionMob(line)"
                        @change="(e) => setLineVerticalPositionMob(
                          currentItem.secondTextLines, lineIndex, e.target.value,
                        )"
                      >
                        <a-radio-button value="top">Top</a-radio-button>
                        <a-radio-button value="bottom">Bottom</a-radio-button>
                        <a-radio-button value="none">Center</a-radio-button>
                      </a-radio-group>
                    </div>
                    <div class="line-options">
                      <a-checkbox
                        v-model:checked="line.isLineWithSquareMob"
                        @change="updatePreview"
                      >
                        With Diamond
                      </a-checkbox>
                      <span class="field-label">Line Color:</span>
                      <a-input
                        v-model:value="line.colorMob"
                        type="color"
                        class="color-input"
                        @input="updatePreview"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a-tab-pane>
        </a-tabs>

        <div class="preview-section">
          <h4>Preview ({{ currentViewMode === 'desktop' ? 'Desktop' : 'Mobile' }})</h4>
          <div
            :class="['preview-container', currentViewMode]"
            :style="{ backgroundColor: previewBackgroundColor }"
          >
            <TitleWithLinesBlock
              v-if="previewItems.length > 0"
              :key="previewKey"
              :text-list="previewItems"
              :is-mobile="currentViewMode === 'mobile'"
              :is-mobile-view="currentViewMode === 'mobile'"
            />
          </div>
        </div>

        <div class="item-actions">
          <a-button
            v-if="localItems.length > 1"
            danger
            @click="removeItem(currentItemIndex)"
          >
            Remove Item {{ currentItemIndex + 1 }}
          </a-button>
        </div>
      </div>

      <!-- Modal Footer -->
      <div :class="['modal-footer', { 'modal-footer--with-delete': showDelete }]">
        <a-button
          v-if="showDelete"
          type="primary"
          danger
          @click="handleDelete"
        >
          Delete
        </a-button>
        <div class="modal-footer__right">
          <a-button @click="handleCancel">Cancel</a-button>
          <a-button type="primary" @click="handleSave">Save</a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="scss">
.title-lines-editor {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
}

.items-list {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding-bottom: 10px;
  border-bottom: 1px solid #e8e8e8;
}

.item-tab {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #40a9ff;
    color: #40a9ff;
  }

  &.active {
    border-color: #1890ff;
    color: #1890ff;
    background: #e6f7ff;
  }
}

.item-editor {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.view-tabs {
  :deep(.ant-tabs-nav) {
    margin-bottom: 16px;
  }
}

.editor-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.text-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .field-label {
    font-weight: 500;
    color: #262626;
  }
}

.layout-settings {
  h4 {
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
  }
}

.checkbox-row {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
}

.alignment-row {
  display: flex;
  align-items: center;
  gap: 12px;

  span {
    font-weight: 500;
  }
}

.color-picker-group {
  display: flex;
  align-items: center;
  gap: 12px;

  .field-label {
    font-weight: 500;
  }

  .color-input {
    width: 60px;
    height: 36px;
    padding: 2px;
    cursor: pointer;
  }

  .color-value {
    font-family: monospace;
    color: #8c8c8c;
  }
}

.lines-config {
  h4 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
  }
}

.lines-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  margin-bottom: 8px;

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
  }
}

.line-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.line-label {
  font-weight: 500;
  color: #262626;
}

.line-settings {
  margin-top: 12px;
  margin-left: 24px;
  padding: 12px;
  background: #fafafa;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 100%;
  overflow-x: hidden;
}

.line-position {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  span {
    font-weight: 500;
  }
}

.line-options {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  .field-label {
    font-weight: 500;
  }

  .color-input {
    width: 50px;
    height: 32px;
    padding: 2px;
    cursor: pointer;
  }
}

.preview-section {
  margin-top: 20px;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 4px;

  h4 {
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 600;
  }
}

.preview-container {
  min-height: 80px;
  padding: 20px;
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;

  &.mobile {
    max-width: 412px;
    margin: 0 auto;
  }

  &.desktop {
    width: 100%;

    @media (min-width: 1921px) {
      width: 1540px;
      margin: 0 auto;
    }
  }
}

.item-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #e8e8e8;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
  margin-top: 20px;

  &--with-delete {
    justify-content: space-between;
  }

  &__right {
    display: flex;
    gap: 12px;
  }
}
</style>
